import { Dropdown, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { COLOR } from "../config/constraits";
import { getAlertConfigsSelect } from "../features/admin/alertValues/api/alertValuesApi";
import { useMemo } from "react";

interface GenerateAlertSelectorProps {
    value: number | null;
    onChange?: (value: number | null) => void;
    disabled?: boolean;
    label?: string;
    minValue?: number;
    maxValue?: number;
    neutralReference?: number;
    alertMinimumValue?: number;
    alertMaximumValue?: number;
    showSelectedLabel?: boolean;
    compactSelected?: boolean;
}

export default function GenerateAlertSelector({
    value,
    onChange,
    disabled = false,
    label,
    showSelectedLabel = true,
    compactSelected = false
}: GenerateAlertSelectorProps) {
    const { t } = useTranslation(['claim']);
    const withAlertLabel = t("claim:withAlert");
    const withoutAlertLabel = t("claim:withoutAlert");

    const { data: alertConfigs = [] } = useQuery({
        queryKey: ['alertConfigsSelect'],
        queryFn: getAlertConfigsSelect,
    });

    const options = useMemo(() => {
        const now = Date.now();
        const latestActiveByValue = new Map<number, { value: number; generatesAlert: boolean; startDate: number }>();

        for (const config of alertConfigs) {
            const parsedStart = Date.parse(config.startDate ?? "");
            const startDate = Number.isNaN(parsedStart) ? 0 : parsedStart;
            if (startDate > now) continue;

            if (config.endDate) {
                const endDate = Date.parse(config.endDate);
                if (!Number.isNaN(endDate) && endDate < now) continue;
            }

            const current = latestActiveByValue.get(config.value);
            if (!current || startDate >= current.startDate) {
                latestActiveByValue.set(config.value, {
                    value: config.value,
                    generatesAlert: config.generatesAlert,
                    startDate
                });
            }
        }

        return Array.from(latestActiveByValue.values())
            .sort((a, b) => a.value - b.value)
            .map((config) => ({
                value: config.value,
                label: `${config.value} - ${config.generatesAlert ? withAlertLabel : withoutAlertLabel}`,
                iconClassName: config.generatesAlert ? "bi bi-exclamation-triangle-fill" : "bi bi-exclamation-circle",
                color: config.generatesAlert ? COLOR.RED : COLOR.TARONJA
            }));
    }, [alertConfigs, withAlertLabel, withoutAlertLabel]);

    const numericValue =
        value !== null ? Number(value) : null;

    const selectedOption = options.find(
        (option) => option.value === numericValue
    );

    const selectedLabel =
        value === null
            ? "-"
            : selectedOption?.label ?? String(value);

    return (
        <Form.Group>
            {label && <Form.Label>{label}</Form.Label>}
            <Dropdown>
                <Dropdown.Toggle
                    variant="light"
                    className={`form-select dropdown-no-caret dropdown-select ${compactSelected ? "justify-content-center" : "dropdown-select-full justify-content-between text-start w-100"} d-flex align-items-center`}
                    disabled={disabled}
                    style={{
                        minHeight: "38px",
                        backgroundColor: disabled ? "#e9ecef" : "#fff",
                        color: disabled ? "#6c757d" : undefined,
                        opacity: 1,
                        ...(compactSelected
                            ? {
                                  width: "64px",
                                  paddingLeft: "0.75rem",
                                  paddingRight: "2rem",
                                  backgroundPosition: "right 0.5rem center"
                              }
                            : {})
                    }}
                >
                    <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {selectedOption ? (
                            <i className={selectedOption.iconClassName} style={{ color: selectedOption.color, fontSize: "16px" }}/>
                        ) : (
                            <span
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    display: "inline-block",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc"
                                }}
                            />
                        )}
                        {showSelectedLabel ? (selectedLabel ?? "-") : null}
                    </span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="w-100 dropdown-menu-text-black">
                    <Dropdown.Item onClick={() => onChange?.(null)}>-</Dropdown.Item>
                    {options.map((option) => (
                        <Dropdown.Item
                            key={option.value}
                            onClick={() => onChange?.(option.value)}
                            className="d-flex align-items-center gap-2"
                        >
                            <i
                                className={option.iconClassName}
                                style={{ color: option.color, fontSize: "16px" }}
                            />
                            {option.label}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </Form.Group>
    );
}
